import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RegistrationStatus } from "../../actions/types";
import { handleAuthApiCall } from "../reducers/registration-reducers";
import authService from "../../services/auth.service";
import { User } from "../../pages/shared/models/user/user";
import { UserTypeEnum } from "../../pages/shared/enum/user-type.enum";
import { ARMENIA_COUNTRY_ID } from "../../constants";

export const registerAction = createAsyncThunk(
  "auth/register",
  async (
    {
      newUserData,
      recipientTypeId,
      tid,
      languageId,
    }: {
      newUserData: User;
      recipientTypeId: number;
      tid: number | null;
      languageId: number;
    },
    thunkApi,
  ) => {
    try {
      const newUserClone = { ...newUserData } as any;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      newUserClone.locationId = newUserClone.locationId.countryId;
      newUserClone.recipientTypeId = recipientTypeId;

      if (recipientTypeId === UserTypeEnum.LEGAL_ENTITY) {
        newUserClone.tid = tid + "";
      }
      newUserClone.email = newUserData.email.toLocaleLowerCase();

      return await authService
        .register(newUserClone, languageId)
        .then((response) => {
          return { userId: response?.data?.id };
        });
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const registerWithGoogleAccountAction = createAsyncThunk(
  "registerWithGoogleAccountAction",
  async ({
    credentials,
    languageId,
  }: {
    credentials: string | undefined;
    languageId: number;
  }) => {
    try {
      return await authService
        .registerWithGoogleAccount(credentials, ARMENIA_COUNTRY_ID, languageId)
        .then((response) => {
          return { userId: response?.data?.id };
        });
    } catch (error) {
      return { error };
    }
  },
);

export const recoverPassword = createAsyncThunk(
  "recoverPassword",
  async (props: { email: string | undefined; languageId: number }) => {
    try {
      return await authService.recoverPassword(props.email, props.languageId);
    } catch (error) {
      return { error };
    }
  },
);

export const confirmResetToken = createAsyncThunk(
  "confirmResetToken",
  async (token: string) => {
    try {
      return await authService.confirmResetToken(token);
    } catch (error) {
      return { error };
    }
  },
);

export const resetPassword = createAsyncThunk(
  "resetPassword",
  async ({
    password,
    token,
  }: {
    password: string | undefined;
    token?: string | null;
  }) => {
    try {
      return await authService.resetPassword(password, token);
    } catch (error) {
      return { error };
    }
  },
);

export const registerSlice = createSlice({
  name: "register",
  initialState: {
    isLoading: false,
    errors: {} as { [key: string]: string },
  },
  reducers: {
    changeErrors: (state, action) => {
      return { ...state, errors: action.payload.errors };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(registerAction.pending, (state) =>
      handleAuthApiCall(state, RegistrationStatus.PENDING, null),
    );
    builder.addCase(registerAction.fulfilled, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.SUCCESS, action.payload),
    );
    builder.addCase(registerAction.rejected, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.FAIL, action.payload),
    );

    builder.addCase(registerWithGoogleAccountAction.pending, (state) =>
      handleAuthApiCall(state, RegistrationStatus.PENDING, null),
    );
    builder.addCase(
      registerWithGoogleAccountAction.fulfilled,
      (state, action) =>
        handleAuthApiCall(state, RegistrationStatus.SUCCESS, action.payload),
    );
    builder.addCase(registerWithGoogleAccountAction.rejected, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.FAIL, action.payload),
    );

    builder.addCase(recoverPassword.pending, (state) =>
      handleAuthApiCall(state, RegistrationStatus.PENDING, null),
    );
    builder.addCase(recoverPassword.fulfilled, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.SUCCESS, action.payload),
    );
    builder.addCase(recoverPassword.rejected, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.FAIL, action.payload),
    );

    builder.addCase(confirmResetToken.pending, (state) =>
      handleAuthApiCall(state, RegistrationStatus.PENDING, null),
    );
    builder.addCase(confirmResetToken.fulfilled, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.SUCCESS, action.payload),
    );
    builder.addCase(confirmResetToken.rejected, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.FAIL, action.payload),
    );

    builder.addCase(resetPassword.pending, (state) =>
      handleAuthApiCall(state, RegistrationStatus.PENDING, null),
    );
    builder.addCase(resetPassword.fulfilled, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.SUCCESS, action.payload),
    );
    builder.addCase(resetPassword.rejected, (state, action) =>
      handleAuthApiCall(state, RegistrationStatus.FAIL, action.payload),
    );
  },
});

export const { changeErrors } = registerSlice.actions;
export default registerSlice.reducer;
